import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Select,
  Tooltip,
  Tabs,
  Collapse,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet, usePost, useDynamicApi } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const { Option } = Select;
const { Panel } = Collapse;

const ExerciseContentManager = () => {
  const { t: translate, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    ageGroup: "",
    theme_manager: "",
    status: "",
  });

  // Pagination and search states
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [exerciseList, setExerciseList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Theme options state
  const [themeOptions, setThemeOptions] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);

  // Form data (main exercise fields)
  const [formData, setFormData] = useState({
    title: "",
    video_url: "",
    assignment_task: "",
    age_group: "",
    theme_manager: "",
    status: "ACTIVE",
    is_variation: false,
    parent_exercise: "",
    variation_title: "",
    attack_defense: "",
    action: "",
    theme: "",
    thumbnail_url: "",
  });

  // Levels data
  const [levels, setLevels] = useState([
    {
      level: "BASIC",
      players_count: null,
      duration_minutes: null,
      rules: [],
      practice_points: [],
      coaching_tips: [],
    },
    {
      level: "NORMAL",
      players_count: null,
      duration_minutes: null,
      rules: [],
      practice_points: [],
      coaching_tips: [],
    },
    {
      level: "CHALLENGING",
      players_count: null,
      duration_minutes: null,
      rules: [],
      practice_points: [],
      coaching_tips: [],
    },
  ]);

  // Form validation errors
  const [formErrors, setFormErrors] = useState({});

  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [parentExercises, setParentExercises] = useState([]);

  // API hooks
  const { execute: fetchList, loading: loadingList } = useGet(
    `${API_ENDPOINTS.EXERCISE_LIST}?page=${currentPage}&page_size=${pageSize}&search=${debouncedSearchTerm}&age_group=${filters.ageGroup || ""}&theme_manager=${filters.theme_manager || ""}&status=${filters.status || ""}`,
  );

  const { execute: fetchThemes, loading: fetchThemesLoading } = useGet(
    API_ENDPOINTS.THEMES_MANAGER_LIST_WITHOUT_PAGINATE,
  );

  const { post, loading: creating } = usePost(API_ENDPOINTS.EXERCISE_LIST);
  const { execute: getById, loading: loadingById } = useDynamicApi("GET");
  const { execute: putData, loading: updating } = useDynamicApi("PATCH");
  const { execute: patchData, loading: updatingStatus } =
    useDynamicApi("PATCH");
  const { execute: deleteData, loading: deleting } = useDynamicApi("DELETE");

  const loading =
    loadingList ||
    loadingById ||
    updating ||
    updatingStatus ||
    deleting ||
    creating ||
    uploading ||
    uploadingThumbnail ||
    submitting ||
    themesLoading;

  // Load data
  const loadData = async () => {
    try {
      const response = await fetchList();
      if (response) {
        setExerciseList(response.data || []);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  // Load theme options based on age group
  const loadThemeOptions = async (ageGroup) => {
    if (!ageGroup) {
      setThemeOptions([]);
      return;
    }
    setThemesLoading(true);
    try {
      const response = await fetchThemes();
      let themes = [];
      if (response?.data && Array.isArray(response.data))
        themes = response.data;
      else if (response && Array.isArray(response)) themes = response;
      const filteredThemes = themes.filter(
        (theme) => theme.age_group === ageGroup,
      );
      const options = filteredThemes.map((theme) => ({
        value: theme.id.toString(),
        label: `${theme.theme_name} - ${theme.component} (${theme.age_group})`,
      }));
      setThemeOptions(options);
    } catch (error) {
      console.error("Error loading themes:", error);
      Toast.error(translate("exerciseManager.failedCreate"));
      setThemeOptions([]);
    } finally {
      setThemesLoading(false);
    }
  };

  // Initial load of all themes (for table display)
  useEffect(() => {
    const loadAllThemes = async () => {
      try {
        const response = await fetchThemes();
        if (response?.data && Array.isArray(response.data)) {
          const options = response.data.map((theme) => ({
            value: theme.id.toString(),
            label: `${theme.theme_name} - ${theme.component} (${theme.age_group})`,
          }));
          setThemeOptions(options);
        } else if (response && Array.isArray(response)) {
          const options = response.map((theme) => ({
            value: theme.id.toString(),
            label: `${theme.theme_name} - ${theme.component} (${theme.age_group})`,
          }));
          setThemeOptions(options);
        }
      } catch (error) {
        console.error("Error loading themes:", error);
      }
    };
    loadAllThemes();
  }, []);

  useEffect(() => {
    if (formData.age_group) {
      loadThemeOptions(formData.age_group);
    } else {
      setThemeOptions([]);
    }
  }, [formData.age_group]);

  useEffect(() => {
    loadData();
  }, [
    debouncedSearchTerm,
    filters.ageGroup,
    filters.theme_manager,
    filters.status,
    currentPage,
    pageSize,
  ]);

  useEffect(() => {
    const parentExercises = exerciseList.filter((ex) => !ex.is_variation);
    setParentExercises(parentExercises);
  }, [exerciseList]);

  // Helper: convert textarea value (lines) to array
  const textareaToArray = (text) => {
    if (!text) return [];
    return text.split("\n").filter((line) => line.trim() !== "");
  };

  const arrayToTextarea = (arr) => {
    if (!arr || !Array.isArray(arr)) return "";
    return arr.join("\n");
  };

  // Level handlers
  const handleLevelFieldChange = (levelIndex, field, value) => {
    const updatedLevels = [...levels];
    if (
      field === "rules" ||
      field === "practice_points" ||
      field === "coaching_tips"
    ) {
      updatedLevels[levelIndex][field] = textareaToArray(value);
    } else {
      updatedLevels[levelIndex][field] = value;
    }
    setLevels(updatedLevels);
  };

  // Main form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name === "age_group") {
      setFormData((prev) => ({ ...prev, theme_manager: "" }));
    }
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (name === "is_variation" && !checked) {
      setFormData((prev) => ({
        ...prev,
        parent_exercise: "",
        variation_title: "",
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // File uploads (same as before, with thumbnail)
  const uploadFile = async (file, type) => {
    if (!file) return null;
    const setUploadingFunc =
      type === "video" ? setUploading : setUploadingThumbnail;
    setUploadingFunc(true);
    try {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("adminToken") ||
        sessionStorage.getItem("access_token");
      if (!token) {
        Toast.error(translate("common.error"));
        setUploadingFunc(false);
        return null;
      }
      const formDataObj = new FormData();
      formDataObj.append("image", file);
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.UPLOAD_MEDIA}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formDataObj,
        },
      );
      const result = await response.json();
      if (response.ok && result.image_url) {
        Toast.success(translate("exerciseManager.uploadSuccess"));
        return result.image_url;
      } else {
        const errorMessage =
          result.message ||
          result.detail ||
          translate("exerciseManager.failedUpload");
        Toast.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      Toast.error(error.message || translate("exerciseManager.failedUpload"));
      return null;
    } finally {
      setUploadingFunc(false);
    }
  };

  const handleVideoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = [
      "video/mp4",
      "video/mov",
      "video/avi",
      "video/mkv",
      "video/webm",
    ];
    if (!validTypes.includes(file.type)) {
      Toast.error(translate("exerciseManager.invalidVideoType"));
      e.target.value = "";
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      Toast.error(translate("exerciseManager.fileSizeExceeded"));
      e.target.value = "";
      return;
    }
    setVideoFile(file);
    Toast.info(translate("exerciseManager.uploadingVideo"));
    const videoUrl = await uploadFile(file, "video");
    if (videoUrl) {
      setFormData((prev) => ({ ...prev, video_url: videoUrl }));
      Toast.success(translate("exerciseManager.uploadComplete"));
    } else {
      setVideoFile(null);
      e.target.value = "";
    }
  };

  const handleThumbnailSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      Toast.error(translate("exerciseManager.invalidImageType"));
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Toast.error(translate("exerciseManager.imageSizeExceeded"));
      e.target.value = "";
      return;
    }
    setThumbnailFile(file);
    Toast.info(translate("exerciseManager.uploadingThumbnail"));
    const thumbnailUrl = await uploadFile(file, "thumbnail");
    if (thumbnailUrl) {
      setFormData((prev) => ({ ...prev, thumbnail_url: thumbnailUrl }));
      Toast.success(translate("exerciseManager.thumbnailUploadComplete"));
    } else {
      setThumbnailFile(null);
      e.target.value = "";
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      video_url: "",
      assignment_task: "",
      age_group: "",
      theme_manager: "",
      status: "ACTIVE",
      is_variation: false,
      parent_exercise: "",
      variation_title: "",
      attack_defense: "",
      action: "",
      theme: "",
      thumbnail_url: "",
    });
    setLevels([
      {
        level: "BASIC",
        players_count: null,
        duration_minutes: null,
        rules: [],
        practice_points: [],
        coaching_tips: [],
      },
      {
        level: "NORMAL",
        players_count: null,
        duration_minutes: null,
        rules: [],
        practice_points: [],
        coaching_tips: [],
      },
      {
        level: "CHALLENGING",
        players_count: null,
        duration_minutes: null,
        rules: [],
        practice_points: [],
        coaching_tips: [],
      },
    ]);
    setFormErrors({});
    setVideoFile(null);
    setThumbnailFile(null);
    setSelectedExercise(null);
    setIsEditMode(false);
  };

  const resetFilters = () => {
    setFilters({ search: "", ageGroup: "", theme_manager: "", status: "" });
    setSearchTerm("");
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim())
      errors.title = translate("exerciseManager.titleRequired");
    else if (formData.title.trim().length < 2)
      errors.title = translate("exerciseManager.titleMinLength");
    else if (formData.title.trim().length > 200)
      errors.title = translate("exerciseManager.titleMaxLength");

    if (!formData.age_group)
      errors.age_group = translate("exerciseManager.ageGroupRequired");
    if (!formData.theme_manager)
      errors.theme_manager = translate("exerciseManager.themeRequired");
    if (!formData.assignment_task.trim())
      errors.assignment_task = translate("exerciseManager.assignmentRequired");
    else if (formData.assignment_task.trim().length < 10)
      errors.assignment_task = translate("exerciseManager.assignmentMinLength");

    if (!formData.video_url && !videoFile)
      errors.video_url = translate("exerciseManager.videoRequired");

    if (formData.is_variation) {
      if (!formData.parent_exercise)
        errors.parent_exercise = translate(
          "exerciseManager.parentExerciseRequired",
        );
      if (!formData.variation_title.trim())
        errors.variation_title = translate(
          "exerciseManager.variationTitleRequired",
        );
      else if (formData.variation_title.trim().length < 2)
        errors.variation_title = translate(
          "exerciseManager.variationTitleMinLength",
        );
    }

    // Optional: validate that at least one level has players/duration? Not required.
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleApiError = (error) => {
    if (error?.response?.data?.errors)
      setFormErrors(error.response.data.errors);
    else if (error?.response?.data?.message)
      Toast.error(error.response.data.message);
    else Toast.error(error?.message || translate("common.error"));
  };

  // Create exercise with levels
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (videoFile && !formData.video_url) {
      Toast.error(translate("exerciseManager.uploadWait"));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        levels: levels.map(
          ({
            level,
            players_count,
            duration_minutes,
            rules,
            practice_points,
            coaching_tips,
          }) => ({
            level,
            players_count: players_count || null,
            duration_minutes: duration_minutes || null,
            rules: rules || [],
            practice_points: practice_points || [],
            coaching_tips: coaching_tips || [],
          }),
        ),
      };
      const response = await post(payload);
      if (response?.status === true) {
        Toast.success(
          response?.message || translate("exerciseManager.createdSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) setFormErrors(response.errors);
        else
          Toast.error(
            response?.message || translate("exerciseManager.failedCreate"),
          );
      }
    } catch (error) {
      console.error("Error creating exercise:", error);
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Update exercise with levels
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedExercise?.id) {
      Toast.error(translate("exerciseManager.idNotFound"));
      return;
    }
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const endpoint = `${API_ENDPOINTS.EXERCISE_UPDATE}/${selectedExercise.id}`;
      const payload = {
        ...formData,
        levels: levels.map(
          ({
            level,
            players_count,
            duration_minutes,
            rules,
            practice_points,
            coaching_tips,
          }) => ({
            level,
            players_count: players_count || null,
            duration_minutes: duration_minutes || null,
            rules: rules || [],
            practice_points: practice_points || [],
            coaching_tips: coaching_tips || [],
          }),
        ),
      };
      const response = await putData(endpoint, payload);
      if (response?.status === true) {
        Toast.success(
          response?.message || translate("exerciseManager.updatedSuccess"),
        );
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        if (response?.errors) setFormErrors(response.errors);
        else
          Toast.error(
            response?.message || translate("exerciseManager.failedUpdate"),
          );
      }
    } catch (error) {
      console.error("Error updating exercise:", error);
      handleApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (exercise) => {
    try {
      const newStatus = exercise.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const endpoint = `${API_ENDPOINTS.EXERCISE_UPDATE}/${exercise.id}`;
      const response = await patchData(endpoint, { status: newStatus });
      if (response?.status === true) {
        Toast.success(
          response?.message ||
            (newStatus === "ACTIVE"
              ? translate("common.active")
              : translate("common.inactive")),
        );
        await loadData();
      } else {
        Toast.error(
          response?.message || translate("exerciseManager.failedUpdateStatus"),
        );
      }
    } catch (error) {
      console.error("Error updating exercise status:", error);
      Toast.error(
        error.message || translate("exerciseManager.failedUpdateStatus"),
      );
    }
  };

  const openDeleteModal = (exercise) => {
    setExerciseToDelete(exercise);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!exerciseToDelete) return;
    try {
      const endpoint = `${API_ENDPOINTS.EXERCISE_UPDATE}/${exerciseToDelete.id}`;
      const response = await deleteData(endpoint);
      if (response?.status === true) {
        Toast.success(
          response?.message || translate("exerciseManager.deletedSuccess"),
        );
        await loadData();
      } else {
        Toast.error(
          response?.message || translate("exerciseManager.failedDelete"),
        );
      }
    } catch (error) {
      console.error("Error deleting exercise:", error);
      Toast.error(error.message || translate("exerciseManager.failedDelete"));
    } finally {
      setIsDeleteModalOpen(false);
      setExerciseToDelete(null);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const fetchExerciseById = async (id) => {
    const endpoint = `${API_ENDPOINTS.EXERCISE_UPDATE}/${id}`;
    return await getById(endpoint);
  };

  const openEditModal = async (exercise) => {
    try {
      const response = await fetchExerciseById(exercise.id);
      if (response?.status === true && response.data) {
        const data = response.data;
        setSelectedExercise(data);
        setFormData({
          title: data.title || "",
          video_url: data.video_url || "",
          assignment_task: data.assignment_task || "",
          age_group: data.age_group || "",
          theme_manager: data.theme_manager || "",
          status: data.status || "ACTIVE",
          is_variation: data.is_variation || false,
          parent_exercise: data.parent_exercise || "",
          variation_title: data.variation_title || "",
          attack_defense: data.attack_defense || "",
          action: data.action || "",
          theme: data.theme || "",
          thumbnail_url: data.thumbnail_url || "",
        });
        // Populate levels
        if (data.levels && Array.isArray(data.levels)) {
          const updatedLevels = levels.map((levelObj) => {
            const existing = data.levels.find(
              (l) => l.level === levelObj.level,
            );
            if (existing) {
              return {
                ...levelObj,
                players_count: existing.players_count || null,
                duration_minutes: existing.duration_minutes || null,
                rules: existing.rules || [],
                practice_points: existing.practice_points || [],
                coaching_tips: existing.coaching_tips || [],
              };
            }
            return levelObj;
          });
          setLevels(updatedLevels);
        } else {
          // Reset levels to empty
          setLevels(
            levels.map((l) => ({
              ...l,
              players_count: null,
              duration_minutes: null,
              rules: [],
              practice_points: [],
              coaching_tips: [],
            })),
          );
        }
        setVideoFile(null);
        setThumbnailFile(null);
        setIsEditMode(true);
        setIsModalOpen(true);
      } else {
        Toast.error(translate("exerciseManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error("Error fetching exercise details:", error);
      Toast.error(
        error.message || translate("exerciseManager.failedFetchDetails"),
      );
    }
  };

  const openViewPage = (id) => {
    navigate(`/exercises/details/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getChoiceLabel = (value, choices) => {
    const choice = choices.find((c) => c.value === value);
    return choice ? choice.label : value;
  };

  const AGE_GROUP_CHOICES = [
    { value: "6-9", label: "6-9 years" },
    { value: "10-14", label: "10-14 years" },
    { value: "15-18", label: "15-18 years" },
  ];

  const STATUS_CHOICES = [
    { value: "ACTIVE", label: translate("common.active") },
    { value: "INACTIVE", label: translate("common.inactive") },
  ];

  const ATTACK_DEFENSE_CHOICES = [
    { value: "ATTACK", label: "Attack" },
    { value: "DEFENSE", label: "Defense" },
    { value: "BOTH", label: "Both" },
  ];

  const ACTION_CHOICES = [
    { value: "MOBILITY_STABILITY", label: "Mobility & Stability" },
    { value: "COORDINATION", label: "Coordination" },
    { value: "POWER_ACTIVATION", label: "Power Activation" },
    { value: "CARDIO", label: "Cardio" },
    { value: "SPORT_SPECIFIC", label: "Sport Specific" },
    { value: "GROUP_DYNAMICS", label: "Group Dynamics" },
  ];

  const THEME_CHOICES = [
    { value: "WARMUP", label: "Warm-up" },
    { value: "SKILLS", label: "Skills" },
    { value: "GAMES", label: "Games" },
    { value: "ATTACK_THEME", label: "Attack Theme" },
    { value: "DEFENSE_THEME", label: "Defense Theme" },
  ];

  const columns = [
    {
      title: "Sr. No.",
      key: "serial",
      render: (_value, _record, index) =>
        (currentPage - 1) * pageSize + index + 1,
      width: 70,
      align: "center",
    },
    {
      title: translate("exerciseManager.title"),
      dataIndex: "title",
      key: "title",
      render: (titleValue, row) => (
        <>
          <strong>{titleValue || "N/A"}</strong>
          {row.is_variation && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {translate("exerciseManager.variation")}
            </Tag>
          )}
        </>
      ),
    },
    {
      title: translate("exerciseManager.ageGroup"),
      key: "age_group",
      render: (_, record) =>
        getChoiceLabel(record.age_group, AGE_GROUP_CHOICES) || "N/A",
    },
    {
      title: translate("exerciseManager.theme"),
      key: "theme_manager",
      render: (_, record) => {
        if (themeOptions.length > 0 && record.theme_manager) {
          const theme = themeOptions.find(
            (theme) => theme.value === record.theme_manager.toString(),
          );
          return theme?.label || record.theme_manager || "N/A";
        }
        return record.theme_manager || "N/A";
      },
    },
    {
      title: translate("exerciseManager.total_likes"),
      key: "total_likes",
      render: (_, record) => record.total_likes || "0",
    },
    {
      title: translate("common.status"),
      dataIndex: "status",
      key: "status",
      render: (statusValue) => (
        <Tag color={statusValue === "ACTIVE" ? "green" : "default"}>
          {getChoiceLabel(statusValue, STATUS_CHOICES)}
        </Tag>
      ),
    },
    {
      title: translate("common.createdAt"),
      dataIndex: "created_at",
      key: "created_at",
      render: (dateValue) => formatDate(dateValue),
    },
    {
      title: translate("common.lastUpdated"),
      dataIndex: "updated_at",
      key: "updated_at",
      render: (dateValue) => formatDate(dateValue),
    },
    {
      title: translate("common.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip
            title={
              record.status === "ACTIVE"
                ? translate("common.inactive")
                : translate("common.active")
            }
          >
            <Button
              className="btn-toggle-active"
              size="small"
              danger={record.status === "ACTIVE"}
              type={record.status === "ACTIVE" ? "default" : "primary"}
              icon={<PoweroffOutlined />}
              onClick={() => handleToggleStatus(record)}
              disabled={updatingStatus}
            />
          </Tooltip>
          <Tooltip title={translate("common.view")}>
            <Button
              className="btn-view"
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewPage(record.id)}
            />
          </Tooltip>
          <Tooltip title={translate("common.edit")}>
            <Button
              className="btn-edit"
              type="primary"
              size="small"
              style={{ background: "#52c41a", borderColor: "#52c41a" }}
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title={translate("common.delete")}>
            <Button
              className="btn-delete"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const hasFilters = searchTerm || Object.values(filters).some((f) => f);

  return (
    <>
      <Card
        title={translate("exerciseManager.titlemain")}
        extra={
          <Button type="primary" onClick={openCreateModal} disabled={loading}>
            {translate("exerciseManager.createNew")}
          </Button>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input
            placeholder={translate("exerciseManager.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ width: 280 }}
            disabled={loading}
          />
          <Select
            placeholder={translate("exerciseManager.ageGroup")}
            value={filters.ageGroup || undefined}
            onChange={(v) => setFilters((p) => ({ ...p, ageGroup: v || "" }))}
            style={{ width: 140 }}
            allowClear
            options={AGE_GROUP_CHOICES}
            disabled={loading}
          />
          <Select
            placeholder={translate("exerciseManager.theme")}
            value={filters.theme_manager || undefined}
            onChange={(v) =>
              setFilters((p) => ({ ...p, theme_manager: v || "" }))
            }
            style={{ width: 200 }}
            allowClear
            loading={themesLoading}
            options={themeOptions}
            disabled={loading || themesLoading}
          />
          <Select
            placeholder={translate("common.filterByStatus")}
            value={filters.status || undefined}
            onChange={(v) => setFilters((p) => ({ ...p, status: v || "" }))}
            style={{ width: 110 }}
            allowClear
            options={STATUS_CHOICES}
            disabled={loading}
          />
          {hasFilters && (
            <Button onClick={resetFilters} disabled={loading}>
              {translate("common.clearFilters")}
            </Button>
          )}
        </Space>

        <div className="table-responsive">
          <Table
            dataSource={exerciseList}
            columns={columns}
            rowKey="id"
            loading={loading}
            locale={{
              emptyText: hasFilters
                ? translate("common.noMatchingEntries")
                : translate("exerciseManager.noExercisesFound"),
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalCount,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              showTotal: (total) =>
                `${translate("common.showing")} ${(currentPage - 1) * pageSize + 1} ${translate("common.to")} ${Math.min(currentPage * pageSize, total)} ${translate("common.of")} ${total} ${translate("exerciseManager.exercises")}`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              },
            }}
          />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={
            isEditMode
              ? translate("exerciseManager.editTitle")
              : translate("exerciseManager.createTitle")
          }
          onConfirm={isEditMode ? handleUpdate : handleCreate}
          confirmText={
            isEditMode
              ? submitting
                ? translate("exerciseManager.updating")
                : translate("common.update")
              : submitting
                ? translate("exerciseManager.creating")
                : translate("common.create")
          }
          showConfirm={true}
          size="large"
          confirmDisabled={submitting || uploading || uploadingThumbnail}
        >
          <form
            className="modal-form"
            onSubmit={isEditMode ? handleUpdate : handleCreate}
          >
            {/* Main Exercise Fields (existing) */}
            <div className="form-group">
              <label htmlFor="title">
                {translate("exerciseManager.title")}{" "}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={translate("exerciseManager.enterTitle")}
                required
                disabled={submitting}
                className={formErrors.title ? "input-error" : ""}
                maxLength={200}
              />
              <div
                style={{ fontSize: "12px", color: "#666", textAlign: "right" }}
              >
                {formData.title.length}/200 {translate("common.characters")}
              </div>
              {formErrors.title && (
                <div className="error-message">{formErrors.title}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="age_group">
                {translate("exerciseManager.ageGroup")}{" "}
                <span className="required">*</span>
              </label>
              <select
                id="age_group"
                name="age_group"
                value={formData.age_group}
                onChange={handleInputChange}
                disabled={submitting}
                required
                className={formErrors.age_group ? "input-error" : ""}
              >
                <option value="">
                  {translate("exerciseManager.selectAgeGroup")}
                </option>
                {AGE_GROUP_CHOICES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {formErrors.age_group && (
                <div className="error-message">{formErrors.age_group}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="theme_manager">
                {translate("exerciseManager.theme")}{" "}
                <span className="required">*</span>
              </label>
              <select
                id="theme_manager"
                name="theme_manager"
                value={formData.theme_manager}
                onChange={handleInputChange}
                disabled={!formData.age_group || submitting || themesLoading}
                required
                className={formErrors.theme_manager ? "input-error" : ""}
              >
                <option value="">
                  {!formData.age_group
                    ? translate("exerciseManager.selectAgeGroupFirst")
                    : themesLoading
                      ? translate("common.loading")
                      : translate("exerciseManager.selectTheme")}
                </option>
                {themeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {formErrors.theme_manager && (
                <div className="error-message">{formErrors.theme_manager}</div>
              )}
            </div>

            {/* New fields: Attack/Defense, Action, Theme (from Exercise model) */}
            {/* <div className="form-group">
              <label htmlFor="attack_defense">
                {translate("exerciseManager.attackDefense")}
              </label>
              <select
                id="attack_defense"
                name="attack_defense"
                value={formData.attack_defense}
                onChange={handleInputChange}
                disabled={submitting}
              >
                <option value="">{translate("common.select")}</option>
                {ATTACK_DEFENSE_CHOICES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div> */}

            {/* <div className="form-group">
              <label htmlFor="action">
                {translate("exerciseManager.action")}
              </label>
              <select
                id="action"
                name="action"
                value={formData.action}
                onChange={handleInputChange}
                disabled={submitting}
              >
                <option value="">{translate("common.select")}</option>
                {ACTION_CHOICES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div> */}

            {/* <div className="form-group">
              <label htmlFor="theme">
                {translate("exerciseManager.themeCategory")}
              </label>
              <select
                id="theme"
                name="theme"
                value={formData.theme}
                onChange={handleInputChange}
                disabled={submitting}
              >
                <option value="">{translate("common.select")}</option>
                {THEME_CHOICES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div> */}

            <div className="form-group">
              <label htmlFor="assignment_task">
                {translate("exerciseManager.assignment")}{" "}
                <span className="required">*</span>
              </label>
              <textarea
                id="assignment_task"
                name="assignment_task"
                value={formData.assignment_task}
                onChange={handleInputChange}
                placeholder={translate("exerciseManager.assignmentPlaceholder")}
                required
                disabled={submitting}
                rows="4"
                className={formErrors.assignment_task ? "input-error" : ""}
              />
              {formErrors.assignment_task && (
                <div className="error-message">
                  {formErrors.assignment_task}
                </div>
              )}
            </div>

            {/* Video Upload */}
            <div className="form-group">
              <label htmlFor="video_file">
                {translate("exerciseManager.uploadVideo")}{" "}
                <span className="required">*</span>
              </label>
              <div className="file-upload">
                <input
                  type="file"
                  id="video_file"
                  accept="video/*"
                  onChange={handleVideoSelect}
                  disabled={submitting || uploading}
                />
                {videoFile && (
                  <div className="file-info">
                    <span className="file-name">{videoFile.name}</span>
                    <span className="file-size">
                      {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                )}
                {uploading && (
                  <div className="upload-progress">
                    <div className="progress-text">
                      {translate("exerciseManager.uploadingVideo")}
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                  </div>
                )}
                {formData.video_url && !uploading && (
                  <div className="upload-success">
                    <span className="success-icon">✓</span>
                    <span>{translate("exerciseManager.uploadComplete")}</span>
                  </div>
                )}
              </div>
              {formErrors.video_url && (
                <div className="error-message">{formErrors.video_url}</div>
              )}
              <div className="form-help">
                {translate("exerciseManager.videoHelp")}
              </div>
            </div>

            {/* Thumbnail Upload */}
            <div className="form-group">
              <label htmlFor="thumbnail_file">
                {translate("exerciseManager.uploadThumbnail")}
              </label>
              <div className="file-upload">
                <input
                  type="file"
                  id="thumbnail_file"
                  accept="image/*"
                  onChange={handleThumbnailSelect}
                  disabled={submitting || uploadingThumbnail}
                />
                {thumbnailFile && (
                  <div className="file-info">
                    <span className="file-name">{thumbnailFile.name}</span>
                    <span className="file-size">
                      {(thumbnailFile.size / 1024).toFixed(2)} KB
                    </span>
                  </div>
                )}
                {uploadingThumbnail && (
                  <div className="upload-progress">
                    <div className="progress-text">
                      {translate("exerciseManager.uploadingThumbnail")}
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                  </div>
                )}
                {formData.thumbnail_url && !uploadingThumbnail && (
                  <div className="upload-success">
                    <span className="success-icon">✓</span>
                    <span>
                      {translate("exerciseManager.thumbnailUploadComplete")}
                    </span>
                  </div>
                )}
              </div>
              <div className="form-help">
                {translate("exerciseManager.thumbnailHelp")}
              </div>
            </div>

            {/* Variation Section (unchanged) */}
            <div className="form-group variation-section">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="is_variation"
                  name="is_variation"
                  checked={formData.is_variation}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <label htmlFor="is_variation">
                  {translate("exerciseManager.isVariation")}
                </label>
              </div>
              {formData.is_variation && (
                <div className="variation-fields">
                  <div className="form-group">
                    <label htmlFor="parent_exercise">
                      {translate("exerciseManager.parentExercise")} *
                    </label>
                    <select
                      id="parent_exercise"
                      name="parent_exercise"
                      value={formData.parent_exercise}
                      onChange={handleInputChange}
                      required={formData.is_variation}
                      disabled={submitting}
                      className={
                        formErrors.parent_exercise ? "input-error" : ""
                      }
                    >
                      <option value="">
                        {translate("exerciseManager.selectParentExercise")}
                      </option>
                      {parentExercises.map((exercise) => (
                        <option key={exercise.id} value={exercise.id}>
                          {exercise.title}
                        </option>
                      ))}
                    </select>
                    {formErrors.parent_exercise && (
                      <div className="error-message">
                        {formErrors.parent_exercise}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="variation_title">
                      {translate("exerciseManager.variationTitle")} *
                    </label>
                    <input
                      type="text"
                      id="variation_title"
                      name="variation_title"
                      value={formData.variation_title}
                      onChange={handleInputChange}
                      placeholder={translate(
                        "exerciseManager.variationPlaceholder",
                      )}
                      required={formData.is_variation}
                      disabled={submitting}
                      className={
                        formErrors.variation_title ? "input-error" : ""
                      }
                    />
                    {formErrors.variation_title && (
                      <div className="error-message">
                        {formErrors.variation_title}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="form-group">
              <label htmlFor="status">{translate("common.status")}</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={submitting}
              >
                {STATUS_CHOICES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* ================= LEVELS SECTION ================= */}
            <div
              style={{
                marginTop: 24,
                borderTop: "1px solid #f0f0f0",
                paddingTop: 16,
              }}
            >
              <h3>{translate("exerciseManager.levelsTitle")}</h3>
              <Collapse defaultActiveKey={["BASIC"]} accordion>
                {levels.map((levelObj, idx) => (
                  <Panel
                    header={`${levelObj.level} ${translate("exerciseManager.level")}`}
                    key={levelObj.level}
                  >
                    <div className="form-group">
                      <label>{translate("exerciseManager.playersCount")}</label>
                      <Input
                        type="number"
                        min={0}
                        value={levelObj.players_count || ""}
                        onChange={(e) =>
                          handleLevelFieldChange(
                            idx,
                            "players_count",
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                        placeholder={translate(
                          "exerciseManager.playersCountPlaceholder",
                        )}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        {translate("exerciseManager.durationMinutes")}
                      </label>
                      <Input
                        type="number"
                        min={0}
                        value={levelObj.duration_minutes || ""}
                        onChange={(e) =>
                          handleLevelFieldChange(
                            idx,
                            "duration_minutes",
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                        placeholder={translate(
                          "exerciseManager.durationPlaceholder",
                        )}
                      />
                    </div>
                    <div className="form-group">
                      <label>{translate("exerciseManager.rules")}</label>
                      <textarea
                        rows={3}
                        value={arrayToTextarea(levelObj.rules)}
                        onChange={(e) =>
                          handleLevelFieldChange(idx, "rules", e.target.value)
                        }
                        placeholder={translate(
                          "exerciseManager.rulesPlaceholder",
                        )}
                      />
                      <div className="form-help">
                        {translate("exerciseManager.rulesHelp")}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>
                        {translate("exerciseManager.practicePoints")}
                      </label>
                      <textarea
                        rows={3}
                        value={arrayToTextarea(levelObj.practice_points)}
                        onChange={(e) =>
                          handleLevelFieldChange(
                            idx,
                            "practice_points",
                            e.target.value,
                          )
                        }
                        placeholder={translate(
                          "exerciseManager.practicePointsPlaceholder",
                        )}
                      />
                      <div className="form-help">
                        {translate("exerciseManager.practicePointsHelp")}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>{translate("exerciseManager.coachingTips")}</label>
                      <textarea
                        rows={3}
                        value={arrayToTextarea(levelObj.coaching_tips)}
                        onChange={(e) =>
                          handleLevelFieldChange(
                            idx,
                            "coaching_tips",
                            e.target.value,
                          )
                        }
                        placeholder={translate(
                          "exerciseManager.coachingTipsPlaceholder",
                        )}
                      />
                      <div className="form-help">
                        {translate("exerciseManager.coachingTipsHelp")}
                      </div>
                    </div>
                  </Panel>
                ))}
              </Collapse>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal (unchanged) */}
      {isDeleteModalOpen && exerciseToDelete && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setExerciseToDelete(null);
          }}
          title={translate("exerciseManager.confirmDelete")}
          onConfirm={handleDeleteConfirm}
          confirmText={
            deleting ? translate("common.deleting") : translate("common.delete")
          }
          confirmColor="danger"
          confirmDisabled={deleting}
        >
          <div className="delete-confirmation">
            <div className="warning-icon">⚠️</div>
            <h3>{translate("exerciseManager.confirmDeleteMessage")}</h3>
            <div className="item-details">
              <p>
                <strong>{translate("exerciseManager.title")}:</strong>{" "}
                {exerciseToDelete.title}
              </p>
              <p>
                <strong>{translate("exerciseManager.ageGroup")}:</strong>{" "}
                {getChoiceLabel(
                  exerciseToDelete.age_group,
                  AGE_GROUP_CHOICES,
                ) || "N/A"}
              </p>
              <p>
                <strong>{translate("exerciseManager.theme")}:</strong>{" "}
                {(() => {
                  if (
                    themeOptions.length > 0 &&
                    exerciseToDelete.theme_manager
                  ) {
                    const theme = themeOptions.find(
                      (t) =>
                        t.value === exerciseToDelete.theme_manager.toString(),
                    );
                    return (
                      theme?.label || exerciseToDelete.theme_manager || "N/A"
                    );
                  }
                  return exerciseToDelete.theme_manager || "N/A";
                })()}
              </p>
              <p>
                <strong>{translate("common.status")}:</strong>{" "}
                <span
                  className={`status-badge ${exerciseToDelete.status === "ACTIVE" ? "active" : "inactive"}`}
                >
                  {getChoiceLabel(exerciseToDelete.status, STATUS_CHOICES)}
                </span>
              </p>
              <p>
                <strong>{translate("common.createdAt")}:</strong>{" "}
                {formatDate(exerciseToDelete.created_at)}
              </p>
            </div>
            <p className="warning-text">
              {translate("exerciseManager.deleteWarning")}
            </p>
            {exerciseToDelete.is_variation && (
              <p className="warning-note">
                <strong>{translate("common.note")}:</strong>{" "}
                {translate("exerciseManager.variationDeleteNote")}
              </p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default ExerciseContentManager;
